import { useState } from "react";
import {
  MEASUREMENT_REVIEW_STATUS_LABEL,
  MEASUREMENT_REVIEW_STATUSES,
  type MeasurementReviewStatus,
} from "@care-by-ella/contracts";
import {
  reviewMeasurementSession,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { MEASUREMENT_REVIEW_TONE } from "@/lib/measurement-actions";
import { STATUS_TONE_CLASS } from "@/lib/status-tone";
import { StatusTag } from "./StatusTag";
import { useReadOnly } from "./SupportView";

// TES-1056 (TES-1054 point F) — the doctor's assessment of one of her blood
// pressure / pulse occasions, with her three labels verbatim (contracts
// MEASUREMENT_REVIEW_STATUS_LABEL). Only the status changes; her values are
// never touched.

export function MeasurementReviewTag({
  status,
}: {
  status: MeasurementReviewStatus;
}) {
  return (
    <StatusTag tone={MEASUREMENT_REVIEW_TONE[status]}>
      {MEASUREMENT_REVIEW_STATUS_LABEL[status]}
    </StatusTag>
  );
}

/**
 * The tag, and for the doctor a choice to change it. In the admin's
 * read-only support view (TES-1040) only the tag is shown.
 */
export function MeasurementReviewControl({
  patientId,
  sessionId,
  status,
  onChanged,
  onUnauthorized,
}: {
  patientId: string;
  sessionId: string;
  status: MeasurementReviewStatus;
  onChanged: (status: MeasurementReviewStatus) => void;
  onUnauthorized?: () => void;
}) {
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function change(next: MeasurementReviewStatus) {
    if (next === status) return;
    setBusy(true);
    setError(null);
    try {
      const res = await reviewMeasurementSession(patientId, sessionId, next);
      onChanged(res.reviewStatus);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-xs">
      {readOnly ? (
        <MeasurementReviewTag status={status} />
      ) : (
        // For the doctor the tag IS the choice: the same pill, opening the
        // three statuses, so the status is never shown twice.
        <select
          aria-label="Bedömning av mätningen"
          value={status}
          disabled={busy}
          onChange={(e) =>
            void change(e.target.value as MeasurementReviewStatus)
          }
          className={`rounded-full border-0 px-sm py-[2px] text-xs font-semibold disabled:opacity-50 ${
            STATUS_TONE_CLASS[MEASUREMENT_REVIEW_TONE[status]]
          }`}
        >
          {MEASUREMENT_REVIEW_STATUSES.map((s) => (
            <option key={s} value={s}>
              {MEASUREMENT_REVIEW_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      )}
      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </span>
  );
}
